/** @file Page to whitelist merchant domains */
import React, { useState } from 'react';
import { AuthPermissions } from 'skipify-types';

import { Dns } from '@mui/icons-material';
import { AddDomainModal } from './components/AddDomainModal';
import DomainDeleteModal from './components/DomainDeleteModal';
import PageWrapper from '../../common/PageWrapper';
import Results from './components/Results';
import { Domain } from '../../store/modules/merchant/types';
import { testId } from './util';
import {
  useCreateDomainMutation,
  useGetDomainsQuery,
  useUpdateDomainMutation,
} from '../../store/modules/merchant/api';
import { toast } from '../../util/toast';
import { isServerError } from '../../util/helpers';
import CanAccess from '../../common/CanAccess';
import { useMerchant } from '../../contexts/MerchantContext';
import { EmptyPage } from '../../common/EmptyPage';

const descriptionText =
  'Which domains do you wish to approve for authorized access to your data? Add the root domains you need to whitelist for additional fraud protection.';

const handleDomainError = (err: unknown) => {
  let errMessage = 'An error occurred while processing your request. Please try again later.';
  if (isServerError(err) && err.status === 400) {
    errMessage = 'The domain must be a valid URL.';
  }
  toast.error(errMessage);
};

/**
 * Main page for configuring a Merchant's whitelisted domains
 */
export const MerchantDomains = () => {
  const { merchantId, isLoading: isLoadingMerchant } = useMerchant();
  const { data: domains, isLoading: isLoadingDomains } = useGetDomainsQuery(merchantId, {
    skip: !merchantId,
  });
  const [createDomain] = useCreateDomainMutation();
  const [updateDomain] = useUpdateDomainMutation();

  const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
  const [domainToEdit, setDomainToEdit] = useState<Domain | undefined>();
  const [domainToDelete, setDomainToDelete] = useState<Domain | null>(null);

  const handleCloseDomainModal = () => {
    setIsDomainModalOpen(false);
    setDomainToEdit(undefined);
  };

  const handleOpenAddModal = () => {
    setDomainToEdit(undefined);
    setIsDomainModalOpen(true);
  };

  const handleOpenEditModal = (domain: Domain) => {
    setDomainToEdit(domain);
    setIsDomainModalOpen(true);
  };

  const handleSaveDomain = (url: string) => {
    if (domainToEdit) {
      return updateDomain({ urlId: domainToEdit.id, merchantId, url })
        .unwrap()
        .then(() => {
          handleCloseDomainModal();
          toast.success('Merchant domain has been updated');
        })
        .catch((err) => handleDomainError(err));
    }

    return createDomain({ merchantId, url })
      .unwrap()
      .then(() => {
        handleCloseDomainModal();
        toast.success('Merchant domain has been created');
      })
      .catch((err) => handleDomainError(err));
  };

  if (!merchantId) {
    return <div />;
  }

  return (
    <PageWrapper
      showParentHierarchyBanner
      data-testid={testId}
      description={descriptionText}
      title="Merchant Domains"
      isLoading={isLoadingDomains || isLoadingMerchant}
    >
      <CanAccess
        permissions={[
          AuthPermissions.UPDATE_MERCHANTS,
          AuthPermissions.UPDATE_CHILD_MERCHANTS_ONLY,
        ]}
      >
        <AddDomainModal
          isOpen={isDomainModalOpen}
          onClose={handleCloseDomainModal}
          onSave={handleSaveDomain}
          domainToEdit={domainToEdit}
        />
      </CanAccess>
      {domainToDelete && (
        <DomainDeleteModal
          open={Boolean(domainToDelete)}
          onClose={() => setDomainToDelete(null)}
          domain={domainToDelete}
        />
      )}
      {domains?.length === 0 ? (
        <EmptyPage
          title="You haven't set up any merchant domains yet."
          paragraph="Get started with your first domain by clicking 'Add merchant domain' below."
          buttonText="Add merchant domain"
          icon={<Dns />}
          onClick={handleOpenAddModal}
        />
      ) : (
        <Results
          domains={domains?.map(({ urlId, url }) => ({ id: urlId, url, isInherited: false }))}
          onAdd={handleOpenAddModal}
          onDelete={(domain) => setDomainToDelete(domain)}
          onEdit={handleOpenEditModal}
        />
      )}
    </PageWrapper>
  );
};
